/**
 * useDashboardsStore - Hook for dashboard management with immediate API sync.
 *
 * All operations are immediately pushed to the server.
 * No local state - the server is the single source of truth.
 */

import { createResourceHook } from "../use-resource-store";
import {
  useDashboards,
  useCreateDashboard,
  useUpdateDashboard,
  useDeleteDashboard,
} from "@/api/dashboard";
import { Dashboard, DashboardCreate } from "@/models/dashboard";

/**
 * Hook for dashboard management with immediate API sync.
 *
 * @example
 * ```tsx
 * function DashboardList() {
 *   const {
 *     resources: dashboards,
 *     create,
 *     update,
 *     remove,
 *     isLoading,
 *     isUpdating,
 *   } = useDashboardsStore();
 *
 *   const handleCreate = async () => {
 *     const dashboard = await create({ name: 'New Dashboard' });
 *   };
 *
 *   const handleUpdate = async (id: string) => {
 *     await update(id, { name: 'Updated Name' });
 *   };
 *
 *   return (
 *     <div>
 *       {dashboards.map(d => (
 *         <DashboardItem key={d.id} dashboard={d} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export const useDashboardsStore = createResourceHook<
  Dashboard,
  DashboardCreate
>({
  resourceName: "dashboard",
  resourcePath: "dashboards",

  useQueryHook: () => {
    const { data, isLoading, error, refetch } = useDashboards();
    return {
      data: data ?? undefined,
      isLoading,
      error: error as Error | null,
      refetch,
    };
  },

  useCreateMutation: () => {
    const mutation = useCreateDashboard();
    return {
      mutateAsync: async (data: DashboardCreate) => {
        const result = await mutation.mutateAsync(data);
        return result as unknown as Dashboard;
      },
    };
  },

  useUpdateMutation: () => {
    const mutation = useUpdateDashboard();
    return {
      mutateAsync: async (
        data: { uriParams: { id: string } } & Record<string, unknown>
      ) => {
        const result = await mutation.mutateAsync(
          data as Parameters<typeof mutation.mutateAsync>[0]
        );
        return result as unknown as Dashboard;
      },
    };
  },

  useDeleteMutation: () => {
    const mutation = useDeleteDashboard();
    return {
      mutateAsync: async (data: { uriParams: { id: string } }) => {
        await mutation.mutateAsync(data);
      },
    };
  },
});

export default useDashboardsStore;
